/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useRef, useEffect, useState, useContext } from 'react';
import styles from './replica.module.css';
import { PageContext, Dimensions, Hotspot, Area } from './Replica.context';
import config from '../../config';

export const Page = (props: PageProps) => {
	const { name, page, hotspots, onClickHotspot, image } = props;
	const ref: any = useRef();
	const [dimensions, setDimensions] = useState<Dimensions | null>(null);
	const [ctx, setCtx] = useState<CanvasRenderingContext2D | null>(null);
	const [hotspotMap, setHotspotMap] = useState<Map<string, Hotspot> | null>(
		null,
	);

	return (
		<PageContext.Provider
			value={{
				containerRef: ref,
				name,
				page,
				dimensions,
				setDimensions,
				hotspotMap,
				setHotspotMap,
				ctx,
				setCtx,
				onClickHotspot,
			}}
		>
			<figure
				id={`page-${name}`}
				ref={ref}
				data-page-number={page}
				data-page-name={name}
				data-hotspots={hotspots.length}
				className={styles['replica-page']}
			>
				<ImageLayer image={image} name={name} />
				<HotspotLayerSvg hotspots={hotspots} />
			</figure>
		</PageContext.Provider>
	);
};

interface PageProps {
	name: string;
	page: number;
	image: string;
	hotspots: Array<Hotspot>;
	onClickHotspot(hotspot: Hotspot): void;
}

const ImageLayer = ({ image, name }: ImageLayerProps) => {
	const ref = useRef<HTMLImageElement | null>(null);
	const contentProtection = config.app.contentProtection;
	const { setDimensions, containerRef, ctx, hotspotMap, onClickHotspot } =
		useContext(PageContext);
	useEffect(() => {
		if (!ref || !containerRef) {
			return;
		}

		const { current } = ref;
		if (!current) {
			return;
		}

		const calculateDimensions = () => {
			setTimeout(() => {
				const { width, height } = current.getBoundingClientRect();
				setDimensions({
					width,
					height,
				});
			}, 500);
		};

		window.addEventListener('orientationchange', () => {
			calculateDimensions();
		});

		window.addEventListener('resize', () => {
			calculateDimensions();
		});

		current.onload = () => {
			calculateDimensions();
		};
	}, [ref]);

	const onClick = (e: any) => {
		if (!ref || !ref.current || !ctx || !hotspotMap) {
			return;
		}
		const { current } = ref;
		const eventLocation = getEventLocation(current, e);
		const pixelData = ctx.getImageData(
			eventLocation.x,
			eventLocation.y,
			1,
			1,
		).data;
		if (
			pixelData[0] == 0 &&
			pixelData[1] == 0 &&
			pixelData[2] == 0 &&
			pixelData[3] == 0
		) {
			return;
		}

		const color =
			'#' +
			(
				'000000' + rgbToHex(pixelData[0], pixelData[1], pixelData[2])
			).slice(-6);
		const hotspot = hotspotMap.get(color);
		if (hotspot) {
			onClickHotspot(hotspot);
		}
	};

	const imageComponent = contentProtection ? (
		// eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
		<img
			alt={name}
			title={name}
			ref={ref}
			src={image}
			onClick={onClick}
			className="content-protection"
			onContextMenu={(e) => e.preventDefault()}
		/>
	) : (
		// eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
		<img alt={name} title={name} ref={ref} src={image} onClick={onClick} />
	);

	return imageComponent;
};

interface ImageLayerProps {
	image: string;
	name: string;
}

export const HotspotsLayer = ({ hotspots }: HotspotsLayerProps) => {
	const {
		hotspotMap,
		setHotspotMap,
		dimensions,
		name,
		ctx,
		setCtx,
		onClickHotspot,
	} = useContext(PageContext);
	const ref = useRef<HTMLCanvasElement>(null);

	useEffect(() => {
		if (!ref || !ref.current) {
			return;
		}
		if (!dimensions) {
			return;
		}

		const { current } = ref;

		setCtx(current.getContext('2d'));
	}, [ref, dimensions]);

	useEffect(() => {
		if (!ctx) {
			return;
		}
		if (!dimensions) {
			return;
		}
		const { width, height } = dimensions;
		ctx.clearRect(0, 0, width, height);
		const colors = getColors(hotspots.length);
		const hMap = new Map<string, Hotspot>();

		for (const hotspot of hotspots) {
			const { area } = hotspot;
			const color = colors.pop();
			if (!color) {
				continue;
			}
			hMap.set(color, hotspot);
			ctx.fillStyle = color;
			const coordinates = getCoordinates(area, width, height);
			ctx.fillRect(
				coordinates.x,
				coordinates.y,
				coordinates.width,
				coordinates.height,
			);
		}

		setHotspotMap(hMap);
	}, [ctx, dimensions]);

	useEffect(() => {
		if (!hotspotMap) {
			return;
		}
		console.log(`Hotspot map`, hotspotMap);
	}, [hotspotMap]);

	if (!hotspots.length) {
		return null;
	}

	if (!dimensions) {
		return null;
	}

	const onClick = (e: React.MouseEvent<HTMLCanvasElement, MouseEvent>) => {
		if (!ref || !ref.current || !ctx || !hotspotMap) {
			return;
		}
		const { current } = ref;
		const eventLocation = getEventLocation(current, e);
		const pixelData = ctx.getImageData(
			eventLocation.x,
			eventLocation.y,
			1,
			1,
		).data;
		if (
			pixelData[0] == 0 &&
			pixelData[1] == 0 &&
			pixelData[2] == 0 &&
			pixelData[3] == 0
		) {
			return;
		}

		const color =
			'#' +
			(
				'000000' + rgbToHex(pixelData[0], pixelData[1], pixelData[2])
			).slice(-6);
		const hotspot = hotspotMap.get(color);
		if (hotspot) {
			onClickHotspot(hotspot);
		}
	};

	return (
		<canvas
			width={dimensions.width}
			height={dimensions.height}
			id={`canvas-${name}`}
			ref={ref}
			onClick={onClick}
		/>
	);
};

const HotspotLayerSvg = ({ hotspots }: HotspotsLayerProps) => {
	const { dimensions } = useContext(PageContext);
	if (!dimensions) {
		return null;
	}

	if (!dimensions.height || !dimensions.width) {
		return null;
	}

	return (
		<svg width={dimensions.width} height={dimensions.height}>
			{hotspots.map((hotspot, i) => (
				<HotspotRec key={i} hotspot={hotspot} />
			))}
		</svg>
	);
};

interface HotspotsLayerProps {
	hotspots: Array<Hotspot>;
}

const HotspotRec = ({ hotspot }: HotspotRecProp) => {
	const { dimensions, onClickHotspot } = useContext(PageContext);
	if (!dimensions) {
		return null;
	}
	const { area } = hotspot;
	const { x, y, width, height } = area;
	const onClick = () => {
		onClickHotspot(hotspot);
	};
	return (
		<rect
			x={x * dimensions.width}
			y={y * dimensions.height}
			onClick={onClick}
			width={width * dimensions.width}
			height={height * dimensions.height}
		/>
	);
};

interface HotspotRecProp {
	hotspot: Hotspot;
}

function getCoordinates(
	area: Area,
	width: number,
	height: number,
): Coordinates {
	const coordinates: Coordinates = {
		x: area.x * width,
		y: area.y * height,
		width: area.width * width,
		height: area.height * height,
	};

	return coordinates;
}

interface Coordinates {
	x: number;
	y: number;
	width: number;
	height: number;
}

function getColors(total: number): Array<string> {
	const colors = new Set<string>();
	do {
		colors.add(getRandomHexColorCode());
	} while (colors.size !== total);
	return [...colors];
}

function getRandomHexColorCode(): string {
	const n = (Math.random() * 0xfffff * 1000000).toString(16);
	return '#' + n.slice(0, 6);
}

function getEventLocation(element: any, event: any) {
	const pos = getElementPosition(element);

	return {
		x: event.pageX - pos.x,
		y: event.pageY - pos.y,
	};
}

function getElementPosition(obj: any): any {
	let curleft = 0,
		curtop = 0;
	if (obj.offsetParent) {
		do {
			curleft += obj.offsetLeft;
			curtop += obj.offsetTop;
		} while ((obj = obj.offsetParent));
		return { x: curleft, y: curtop };
	}
	return undefined;
}

function rgbToHex(r: any, g: any, b: any) {
	if (r > 255 || g > 255 || b > 255)
		throw new Error('Invalid color component');
	return ((r << 16) | (g << 8) | b).toString(16);
}
